import React from 'react'
import BEAGLEGPTLOGO from "../../static/beagle-logo.svg"
import ABOUTCHATMOBILE from "../../static/about-chat-mobile.png"
import CAROUSEL_CONTEXT_RETENTION from "../../static/carousel_context_retention.svg"
import CAROUSEL_NARRATIVE_INSIGHTS from "../../static/carousel_narrative_insights.svg"
import CAROUSEL_NARRATIVE_BOARD from "../../static/carousel_narrative_board.svg"
import CAROUSEL_DATA_OPTION from "../../static/carousel_data_option.svg"
import CAROUSEL_NUDGES from "../../static/carousel_nudges.svg"
import CAROUSEL_NLP from "../../static/carousel_nlp.svg"
import { ABOUTCONST, EMAIL_BEAGLE } from '../../utils/constants'

const FEATURE_CARDS = [
  {
    id: 'context-retention',
    title: 'Context Retention',
    description:
      'Users can easily ask follow-up questions. The context of previous questions is automatically retained throughout the conversation.',
    image: CAROUSEL_CONTEXT_RETENTION,
  },
  {
    id: 'narrative-insights',
    title: 'Narratives with Insights',
    description:
      'Get a concise summary of the most important insights and trends hidden in your data, delivered in plain language.',
    image: CAROUSEL_NARRATIVE_INSIGHTS,
  },
  {
    id: 'narrative-board',
    title: 'Narrative Board',
    description:
      'A visual dashboard that surfaces key metrics and trends at a glance so teams stay aligned on what matters.',
    image: CAROUSEL_NARRATIVE_BOARD,
  },
  {
    id: 'data-options',
    title: 'Data Options',
    description:
      'Connect and query multiple data sources from a single interface, keeping all your data accessible in one place.',
    image: CAROUSEL_DATA_OPTION,
  },
  {
    id: 'nudges',
    title: 'Nudges',
    description:
      'Receive proactive insights and suggestions so opportunities and anomalies are never missed in your dataset.',
    image: CAROUSEL_NUDGES,
  },
  {
    id: 'nlp',
    title: 'NLP',
    description:
      'Ask questions in plain English. Natural language processing translates your queries directly into data answers.',
    image: CAROUSEL_NLP,
  },
]

const About = () => {
  return (
    <div
      className="w-full overflow-y-auto h-full"
      style={{ background: 'var(--bg-primary)' }}
    >

      {/* ── Hero banner ── */}
      <div
        className="relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, var(--bg-secondary), var(--bg-primary))' }}
      >
        <div className="max-w-6xl mx-auto px-6 py-10 lg:py-14 flex flex-col lg:flex-row items-center gap-8">
          {/* Left: text content */}
          <div className="flex-1 flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <img
                src={BEAGLEGPTLOGO}
                alt="BeagleGPT"
                className="w-10 h-10 object-contain"
              />
              <h1 className="text-2xl lg:text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>
                BeagleGPT
              </h1>
            </div>
            <p className="text-lg lg:text-xl font-semibold" style={{ color: 'var(--brand-primary)' }}>
              {ABOUTCONST.GENAIDESC}
            </p>
            <p className="text-sm lg:text-base leading-relaxed max-w-lg" style={{ color: 'var(--text-secondary)' }}>
              {ABOUTCONST.DEEPDIVETEXT}
            </p>
          </div>

          {/* Right: mobile mockup */}
          <div className="hidden lg:block flex-shrink-0">
            <img
              src={ABOUTCHATMOBILE}
              alt="BeagleGPT mobile chat preview"
              className="w-48 xl:w-56 h-auto object-contain drop-shadow-2xl"
            />
          </div>
        </div>
      </div>

      {/* ── Key Features grid ── */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        <h2
          className="text-lg lg:text-xl font-bold mb-6"
          style={{ color: 'var(--text-primary)' }}
        >
          {ABOUTCONST.KEYFEATURES}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {FEATURE_CARDS.map((feature) => (
            <div
              key={feature.id}
              className="group flex flex-col gap-3 rounded-xl border p-5 transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5"
              style={{
                background: 'var(--card-bg)',
                borderColor: 'var(--border-color)',
              }}
            >
              <div
                className="rounded-lg overflow-hidden"
                style={{ background: 'var(--bg-secondary)' }}
              >
                <img
                  src={feature.image}
                  alt={feature.title}
                  className="w-full h-auto object-contain transition-transform duration-200 group-hover:scale-110"
                />
              </div>
              <h3
                className="text-sm font-bold"
                style={{ color: 'var(--brand-primary)' }}
              >
                {feature.title}
              </h3>
              <p
                className="text-xs leading-relaxed"
                style={{ color: 'var(--text-secondary)' }}
              >
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* ── Info cards ── */}
      <div className="max-w-6xl mx-auto px-6 pb-10 grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* For Everyone */}
        <div
          className="rounded-xl border p-6"
          style={{
            background: 'var(--card-bg)',
            borderColor: 'var(--border-color)',
          }}
        >
          <div className="flex items-center gap-2 mb-3">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ background: 'var(--brand-primary)' }}
            >
              <span className="text-white text-sm font-bold">&#10022;</span>
            </div>
            <h3 className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>
              For Everyone
            </h3>
          </div>
          <p
            className="text-sm leading-relaxed"
            style={{ color: 'var(--text-secondary)' }}
            data-testid="role_text"
          >
            {ABOUTCONST.ROLESTEXT}
          </p>
        </div>

        {/* Getting Started */}
        <div
          className="rounded-xl border p-6"
          style={{
            background: 'var(--card-bg)',
            borderColor: 'var(--border-color)',
          }}
        >
          <div className="flex items-center gap-2 mb-3">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ background: 'var(--brand-primary)' }}
            >
              <span className="text-white text-sm font-bold">&#10140;</span>
            </div>
            <h3 className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>
              Getting Started
            </h3>
          </div>
          <p
            className="text-sm leading-relaxed"
            style={{ color: 'var(--text-secondary)' }}
            data-testid="contactus_text"
          >
            {ABOUTCONST.CONTACTUSTEXT}
            <a
              href={`mailto:${EMAIL_BEAGLE}`}
              className="font-bold underline underline-offset-2 transition-colors duration-150"
              style={{ color: 'var(--brand-primary)' }}
            >
              {EMAIL_BEAGLE}
            </a>
            .
          </p>
        </div>
      </div>

    </div>
  )
}

export default About
